import { IAlertStatusType, IAppState, ILog, IMonitorStatusType, IShipStatus, IStatus, IStatusType } from "../../Types"
import { Icons } from "../../Components/Icon"
import { UserAdviceLevel, UserAdviceRole } from "../../Components/UserAdvice/Params"
import { FocusViewState } from "../FocusView"
import { UserAdvice, UserAdviceInstance, UserAdviceLocation, UserAdviceMonitor, UserAdviceState } from "./Advice"
import { OverlayViewDefinition } from "../OverlayView"
import { userAdviceDepthWatchOverlay } from "./DepthWatchView"
import { UserAdviceViewsData } from "./Context"

export class DepthWatchAdvice extends UserAdvice implements UserAdviceMonitor {
  readonly role = UserAdviceRole.monitor;
  active: boolean = false;
  enabled: boolean = false;
  monitor?: IStatus = undefined;
  alerts: IStatus[] = [];
  static level: number = 90;

  // The standalone underway depth watch (#990): independent of any anchor watch,
  // enabled by its mere presence (no method/threshold config of its own -- the
  // underway default threshold always applies). It is configurable on any active
  // day, but only claims a depth alert while the ship status is `inmotion`.
  update(_app: IAppState, state: FocusViewState, currentLog?: ILog) {
    this.active = false;
    this.enabled = false;
    this.monitor = undefined;
    this.alerts = [];
    if (!currentLog) {
      return;
    }
    this.active = true;
    const monitorStatus = UserAdvice.status(state.statuses, IStatusType.monitor, currentLog.dayId)
      .filter((s) => s.item == IMonitorStatusType.depth)
      .toSorted((a, b) => b.id - a.id)[0];
    if (monitorStatus && monitorStatus.endLogId == undefined) {
      this.enabled = true;
      this.monitor = monitorStatus;
    }
    const shipStatus = UserAdvice.status(state.statuses, IStatusType.ship, currentLog.dayId)
      .filter((s) => s.endLogId == undefined)
      .toSorted((a, b) => b.id - a.id)[0];
    if (this.enabled && shipStatus?.value == IShipStatus.inmotion) {
      this.alerts = UserAdvice.status(state.statuses, IStatusType.alert, currentLog.dayId)
        .filter((s) => s.endLogId == undefined && s.name == IAlertStatusType.depth);
    }
  }

  // The monitor-role contract (#916). Unlike the anchor watch, "underway" is not a
  // trackable ship state, so this watch is available on any day with a current log
  // -- which is exactly what `active` already means here.
  isActive(): boolean {
    return this.enabled;
  }

  isAvailable(): boolean {
    return this.active;
  }

  activate(app: IAppState): Promise<void> {
    return this.setEnabled(app, true);
  }

  deactivate(app: IAppState): Promise<void> {
    return this.setEnabled(app, false);
  }

  // Through the dedicated lifecycle route, like the dialog's own submit.
  private async setEnabled(app: IAppState, enabled: boolean): Promise<void> {
    await UserAdviceViewsData.depthWatch(app.apiClient, { enabled });
  }

  getInstances(app: IAppState, location: UserAdviceLocation, currentLog?: ILog): UserAdviceInstance[] {
    // The ship toolbar only (#916) -- same reasoning, and the same
    // location-scoped id, as AnchorMonitorAdvice.getInstances above.
    if (!this.active || !currentLog || location != UserAdviceLocation.toolbar) {
      return [];
    }
    const t = app.getT("UserAdviceDepthWatchView");
    return [
      new UserAdviceInstance(`depth-${location}`, DepthWatchAdvice.level, { icon: Icons.AdviceDepth, level: UserAdviceLevel.notice, shortTitle: t("title_short"), title: t(this.enabled ? "title_active" : "title_inactive"), type: "depth" }, this, 0, location),
    ];
  }

  overlay(_app: IAppState, advice: UserAdviceInstance, state: UserAdviceState): OverlayViewDefinition | undefined {
    return userAdviceDepthWatchOverlay({ advice, state });
  }
}
